import React, { useEffect, useRef } from "react";
import { Fragment } from "react";
import TransitionOnLoad from "Components/TransitionOnLoad";
import { CardBody, Col, Row, Card, Modal, Button, ModalHeader, ModalBody, ModalFooter, } from "reactstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import ReactTableGeneric from "Components/ReactTableGeneric";
import { format } from "date-fns";
import ExportForm from "./ExportForm";
import TextLimit from "Api/TextLimit";
import { useDownloadExport, useExportList, useInsertExportList, useOrganizationsList } from "Api/Melder/Export";
import { downloadBase64File } from "helperFunctions";
import { IoIosRefresh } from "react-icons/io";
import { ReactComponent as PendingIcon } from "../../../../assets/utils/images/svgs/HistoryclockionDarkBlue.svg";


const Compose = () => {
  const [showSweetAlert, setShowSweetAlert] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [documentId, setDocumentId] = React.useState(null);
  const intervalRef = useRef(null);

  var date = new Date();
  let setEndDateperiod = date.setMonth(date.getMonth() + 1);
  const beginDate = new Date();
  const endDate = new Date(setEndDateperiod);

  const {
    data: organizationsResponse,
    isLoading: organizationLoading,
  } = useOrganizationsList();

  const {
    data: exportResponse,
    mutate: mutateExportList,
    isLoading: exportListLoading,
  } = useExportList();

  const totalCount=exportResponse?.totalCount;

  const { trigger: insertExportList } = useInsertExportList();
  const data = organizationsResponse?.data || [];

  const {
    data: downloadExport,
    isLoading: downloadExportLoading,
  } = useDownloadExport(documentId);

  const exportList = exportResponse?.data || [];

  const downloadFile = (rowData) => {
    setDocumentId(rowData.id);
  };


  const initialValues = {
    type: "Dagdelen",
    organization: "",
    school: "",
    dateBeginPeriod: beginDate,
    dateEndPeriod: endDate,
  };

  const validationSchema = Yup.object().shape({
    type: Yup.string().required("Veld mag niet leeg zijn"),
    organization: Yup.string().required("Veld mag niet leeg zijn"),
    school: Yup.string().required("Veld mag niet leeg zijn"),
    dateBeginPeriod: Yup.date().required("Veld mag niet leeg zijn"),
    dateEndPeriod: Yup.date().min(
      Yup.ref("dateBeginPeriod"),
      "End date can't be before Start date"
    ),
  });

  const columns = React.useMemo(
    () => [
      {
        header: () => "ID",
        accessorKey: "id",
      },
      {
        header: () => "Bestuur",
        accessorKey: "vCommunityName",
        cell: (value) => {
          return window.innerWidth > 1250
            ? TextLimit.limitStringLargeScreen(value.row.original.vCommunityName)
            : TextLimit.limitStringSmallScreen(value.row.original.vCommunityName);
        },
      },
      {
        header: () => "Coordinator",
        accessorKey: "vFirstName",
      },
      {
        header: () => "Aanmaakdatum",
        accessorKey: "Created_At",
      },
    
      {
        header: () => "Actie",
        accessorKey: "iStatus",
        cell: ({ row }) => (
          <div className="d-flex ">
            {row.original.iStatus !== 1 ? (
              isRefreshing ? (
                <IoIosRefresh className="spin" color="#025F94" />
              ) : (
                <PendingIcon />
              )
            ) : (
              <a
                href="javascript:void(0)"
                onClick={() => downloadFile(row.original)}
                className="anchor-action-link"
              >
                <div className="font-icon-wrapper weekstaat-table-button text-align-left">
                  <i className="pe-7s-download btn-icon-wrapper"></i>
                </div>
              </a>
            )}
          </div>
        )
      }
    ],
    [isRefreshing]
  );

  useEffect(() => {

    if (!downloadExport) {
      return;
    }
    const fileData = downloadExport?.file?.download;
    const fileType = downloadExport?.file?.extension;

    if (downloadExport?.status && fileData) {

      downloadBase64File(
        fileData,
        `export.${fileType || "xlsx"}`
      );
    } else {
      console.log("Condition failed");
    }
  }, [downloadExport]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const hasPending = exportList.some(item => item.iStatus === 0);

    if (!hasPending) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    if (intervalRef.current) return;

    intervalRef.current = setInterval(async () => {
      setIsRefreshing(true);

      const latest = await mutateExportList();

      setIsRefreshing(false);

      const latestExportList = latest?.data || [];
      const stillPending = latestExportList.some(item => item.iStatus === 0);

      if (!stillPending) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 15000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [exportList, mutateExportList]);

  const handleSubmit = async (values) => {

    try {
      let postdata = { ...values };

      postdata.organization = [postdata.organization];
      postdata.dateBeginPeriod = format(
        new Date(values.dateBeginPeriod),
        "yyyy-MM-dd"
      );
      postdata.dateEndPeriod = format(
        new Date(values.dateEndPeriod),
        "yyyy-MM-dd"
      );

      const response = await insertExportList({
        data: postdata,
      });

      if (response?.status === 200 || response?.data?.status) {
        setShowSweetAlert(true);
        await mutateExportList();

      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Fragment>
      <Card className="main-card mb-3">
        <TransitionOnLoad>
          <CardBody>
            <Row>
              <Col lg="12">
                <ReactTableGeneric
                  data={exportList}
                  columns={columns}
                  tableClassName="mb-0 table table-borderless table-sm table-hover"
                  count={totalCount}
                />
              </Col>
            </Row>
          </CardBody>
        </TransitionOnLoad>
      </Card>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <ExportForm organizations={data} />
      </Formik>
      <Modal
        isOpen={showSweetAlert}
        toggle={() => setShowSweetAlert(false)}
        className="modal-centered contract-type-modal feedback-modal-style link-form"
      >
        <ModalHeader
          toggle={() => setShowSweetAlert(false)}
          className="px-3 mt-4"
        >
          Opmerking
        </ModalHeader>

        <ModalBody className="text-center mt-4 mb-4">
          <p className="contracttype-Modal" style={{ fontWeight: "500" }}>
            Uw exportaanvraag is succesvol ontvangen. De export wordt verwerkt en verschijnt binnen enkele seconden in de lijst.
          </p>
        </ModalBody>

        <ModalFooter className="justify-content-center pt-3">
          <Button
            className="btn-color-style opslaan-btn px-4"
            size="lg"
            outline
            color="primary"
            onClick={() => setShowSweetAlert(false)}

          >
            Sluiten
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default Compose;

